import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import url from '../url.js';
import SimpleNavbar from '../components/SimpleNavbar';
import MessageThread from '../components/MessageThread';
import ActivityAnalysisCharts from '../components/Dashboard/ActivityAnalysisCharts';
import UserSpeakingHabitsTable from '../components/Dashboard/UserSpeakingHabitsTable';
import { Paper, Typography, Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { Group, Groups as GroupsIcon } from '@mui/icons-material';
import semanticColors from '../styles/semanticColors';

export default function Dashboard() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const activityId = searchParams.get('activityId');
    
    const ws = io.connect(url.socketioHost, { path: '/s/socket.io' });
    const [data, setData] = useState({ nodes: [], edges: [] });
    const [groupIds, setGroupIds] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [displayConfig, setDisplayConfig] = useState({
        showMessageThread: true,
        showCharts: true
    });

    // 根據 activityId 查詢所有小組
    useEffect(() => {
        const fetchGroups = async () => {
            if (!activityId) {
                console.error('[Dashboard] 缺少 activityId 參數');
                setLoading(false);
                return;
            }

            try {
                const jwtToken = localStorage.getItem('jwtToken');
                const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};
                
                const response = await axios.get(
                    `${url.backendHost}api/activities/${activityId}`,
                    { headers, withCredentials: true }
                );
                
                const groups = response.data.Groups || [];
                const ids = groups.map(g => g.id).filter(Boolean);
                setGroupIds(ids);
                
                // 自動選擇第一個小組
                if (ids.length > 0 && !selectedGroupId) {
                    setSelectedGroupId(ids[0]);
                }
            } catch (error) {
                console.error('[Dashboard] 獲取小組列表失敗:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchGroups();
    }, [activityId]);

    useEffect(() => {
        if (ws) {
            initWebSocket();
        }
    }, []);

    useEffect(() => {
        if (selectedGroupId) {
            getNodes();
        }
    }, [selectedGroupId]);

    const handleGroupChange = (event) => {
        const newGroupId = event.target.value;
        setSelectedGroupId(newGroupId);
        sessionStorage.setItem('groupId', newGroupId);
        getNodes();
    };

    const getNodes = async () => {
        try {
            if (!selectedGroupId) {
                console.error('[Dashboard] groupId 無效:', selectedGroupId);
                return;
            }
            
            const response = await axios.get(`${url.backendHost}api/nodes/all/${selectedGroupId}`);
            const groupData = response.data[0] || {};
            setData({ 
                nodes: groupData.Nodes || [], 
                edges: groupData.Edges || [] 
            });
        } catch (error) {
            console.error('Error fetching nodes:', error.message);
        }
    };

    const initWebSocket = () => {
        if (!activityId) return;
        ws.on(`node-recieve-${activityId}`, handleNodeReceive);
        ws.on(`edge-recieve-${activityId}`, handleEdgeReceive);
    };

    const handleNodeReceive = (body) => {
        if (body.groupId == selectedGroupId) {
            const normalizedNode = {
                ...body,
                groupId: parseInt(body.groupId, 10),
                activityId: body.activityId ? parseInt(body.activityId, 10) : undefined,
                createdAt: body.createdAt || new Date().toISOString(),
                updatedAt: body.updatedAt || new Date().toISOString(),
            };
            setData(prevData => {
                const updatedNodes = [...prevData.nodes, normalizedNode];
                return { ...prevData, nodes: updatedNodes };
            });
        }
    };

    const handleEdgeReceive = (body) => {
        if (body.groupId == selectedGroupId) {
            const normalizedEdge = {
                ...body,
                groupId: parseInt(body.groupId, 10),
                activityId: body.activityId ? parseInt(body.activityId, 10) : undefined,
                createdAt: body.createdAt || new Date().toISOString(),
                updatedAt: body.updatedAt || new Date().toISOString(),
            };
            setData(prevData => {
                const updatedEdges = [...prevData.edges, normalizedEdge];
                return { ...prevData, edges: updatedEdges };
            });
        }
    };

    // 空狀態組件 - 沒有 activityId
    const NoActivityEmptyState = () => (
        <Paper 
            elevation={2}
            sx={{ 
                padding: 4,
                textAlign: 'center',
                backgroundColor: semanticColors.lightBg,
                borderRadius: 2,
                border: '2px dashed #dee2e6',
                margin: '20px'
            }}
        >
            <Group sx={{ fontSize: 64, color: '#6c757d', mb: 2 }} />
            <Typography variant="h5" component="h2" sx={{ 
                fontWeight: 500,
                color: '#495057',
                mb: 2
            }}>
                缺少活動參數
            </Typography>
            <Typography variant="body1" sx={{ 
                color: '#6c757d',
                mb: 3,
                maxWidth: 500,
                margin: '0 auto'
            }}>
                請從活動列表中選擇「儀表板分析」進入。
            </Typography>
        </Paper>
    );

    // 空狀態組件 - 沒有加入小組
    const NoGroupEmptyState = () => (
        <Paper 
            elevation={2}
            sx={{ 
                padding: 4,
                textAlign: 'center',
                backgroundColor: semanticColors.lightBg,
                borderRadius: 2,
                border: '2px dashed #dee2e6',
                margin: '20px'
            }}
        >
            <Group sx={{ fontSize: 64, color: '#6c757d', mb: 2 }} />
            <Typography variant="h5" component="h2" sx={{ 
                fontWeight: 500,
                color: '#495057',
                mb: 2
            }}>
                目前沒有加入任何小組
            </Typography>
            <Typography variant="body1" sx={{ 
                color: '#6c757d',
                mb: 3,
                maxWidth: 500,
                margin: '0 auto'
            }}>
                請先加入一個小組才能查看儀表板數據。加入小組後，您就可以查看小組的學習活動統計、節點分析和即時討論內容。
            </Typography>
        </Paper>
    );

    return (
        <div className="Dashboard">
            <SimpleNavbar />
            
            {/* 頁首小組選擇器 */}
            {groupIds.length > 0 && (
                <Box sx={{ 
                    position: 'sticky',
                    top: 0,
                    zIndex: 1200,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 2,
                    px: 3,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    pointerEvents: 'auto'
                }}>
                    <Box sx={{ 
                        maxWidth: '1400px',
                        margin: '0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        position: 'relative',
                        zIndex: 1
                    }}>
                        <GroupsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <FormControl size="small" sx={{ minWidth: 200, zIndex: 1 }}>
                            <InputLabel>選擇小組</InputLabel>
                            <Select
                                value={selectedGroupId || ''}
                                onChange={handleGroupChange}
                                label="選擇小組"
                                MenuProps={{
                                    sx: { zIndex: 1300 }
                                }}
                            >
                                {groupIds.map((id) => (
                                    <MenuItem key={id} value={id}>
                                        小組 {id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                            當前小組：{selectedGroupId}
                        </Typography>
                    </Box>
                </Box>
            )}

            {!activityId ? (
                <NoActivityEmptyState />
            ) : loading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography>載入中...</Typography>
                </Box>
            ) : groupIds.length === 0 ? (
                <NoGroupEmptyState />
            ) : (
                <>
                    {displayConfig.showMessageThread && (
                        <div className="message-thread-container" style={{ 
                            width: '100%',
                            maxWidth: '1400px',
                            margin: '20px auto',
                            padding: '0 20px'
                        }}>
                            <MessageThread activityId={activityId} selectedGroupId={selectedGroupId} />
                        </div>
                    )}

                    {displayConfig.showCharts && data.nodes.length > 0 && (
                        <div style={{ 
                            width: '100%',
                            maxWidth: '1400px',
                            margin: '20px auto',
                            padding: '0 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px'
                        }}>
                            <ActivityAnalysisCharts nodes={data.nodes} visible={displayConfig.showCharts} />
                            <UserSpeakingHabitsTable nodes={data.nodes} edges={data.edges} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
